import { handleActions } from "redux-actions";
import { AsyncStates } from "src/constants";
import {
  DeleteMasksActionTypes,
  DeleteSemAnalysisFilesActionTypes,
  DeleteSemAnalysisMaskHeightWidthActionTypes,
  DeletePaintFilmBatchActionTypes,
  DownloadSemAnalysisReportActionTypes,
  FetchClassificationAnalysisActionTypes,
  FetchImageSubtractionActionTypes,
  FetchInstanceDetectionActionTypes,
  FetchSemAnalysisDataActionTypes,
  FetchSemAnalysisListActionTypes,
  FetchSemAnalysisMaskDataActionTypes,
  FetchSemAnalysisMaskHeightWidthActionTypes,
  FetchSemAnalysisScaleActionTypes,
  FetchPaintFilmBatchInfoActionTypes,
  ProcessPaintFilmAnalysisActionTypes,
  ResetMasksActionTypes,
  SetImageSubtractionActionTypes,
  SetSemAnalysisMaskHeightWidthActionTypes,
  PaintFilmRecalibrateScaleActionTypes,
  UpdateClassificationActionTypes,
  AnalysisTypesActionTypes,
  FetchResidualBatchInfoActionTypes,
  DownloadBatchReportActionTypes,
  DeleteResidualBatchActionTypes,
  ResidualRecalibrateScaleActionTypes,
  ModifyPaintFilmActionTypes,
  ProcessFiberAnalysisActionTypes,
  FetchFiberBatchInfoActionTypes,
  DeleteFiberBatchActionTypes,
  FiberRecalibrateScaleActionTypes,
  ProcessWhiteObjectsActionTypes,
  ModifyWhiteObjectsActionTypes,
  ModifyPaintFilmBatchActionTypes,
  ModifyFiberActionTypes,
  DownloadSegmentedImagesTypes,
  PhaseTwoFiberAnalysisActionTypes,
  ProcessBlackObjectsAnalysisActionTypes,
  FetchBlackObjectsBatchInfoActionTypes,
  DeleteBlackObjectsBatchActionTypes,
  BlackObjectsRecalibrateScaleActionTypes,
  ModifyBlackObjectsActionTypes,
  UpdatePhaseTwoFiberAnalysisPhaseActionTypes,
  DownloadFiberAnalysisPhaseTwoReportActionTypes,
  GetFiberAnalysisPhaseTwoParametersActionTypes,
} from "../actions/semAnalysis";


const PhaseTwoDefaultParameters = {
  "basic_parameters": {
    "name": "Basic Parameters",
    "parameters": {
      "adaptive_c": {
        "default": -8,
        "value": -8,
        "name": "Adaptive C Threshold",
        "range": [-20, 20],
        "description": "Adaptive threshold for C value in adaptive thresholding",
      },
      "max_gap_distance": {
        "default": 15,
        "value": 15,
        "name": "Maximum Gap Distance",
        "range": [5, 50],
        "description": "Maximum distance (pixels) between fiber endpoints to consider joining them"
      },
      "min_fiber_length": {
        "default": 10,
        "value": 10,
        "name": "Minimum Fiber Length",
        "range": [5, 50],
        "description": "Fibers shorter than this (pixels) will not be joined with others"
      }
    }
  },
  "junction_parameters": {
    "name": "Junction Parameters",
    "parameters": {
      "junction_sensitivity": {
        "default": 13,
        "value": 13,
        "name": "Junction Sensitivity",
        "range": [10, 20],
        "description": "Higher values detect fewer junctions (threshold for branch point detection)"
      },
      "max_branch_angle": {
        "default": 45.0,
        "value": 45.0,
        "name": "Maximum Branch Angle",
        "range": [10, 90],
        "description": "Maximum angle (degrees) at which branches can split from main fiber"
      },
      "max_junction_distance": {
        "default": 3,
        "value": 3,
        "name": "Maximum Junction Distance",
        "range": [1, 10],
        "description": "Maximum distance (pixels) to consider between branch points"
      },
      "junction_intensity_threshold": {
        "default": 50,
        "value": 50,
        "name": "Junction Intensity Threshold",
        "range": [0, 100],
        "description": "Minimum intensity required at junction points (percentile)"
      }
    }
  },
  "skeleton_parameters": {
    "name": "Skeleton Parameters",
    "parameters": {
      "skeleton_pruning": {
        "default": 0,
        "value": 0,
        "name": "Skeleton Pruning",
        "range": [0, 10],
        "description": "Remove small branches from skeleton (pixels)"
      },
      "smooth_factor": {
        "default": 0,
        "value": 0,
        "name": "Smooth Factor",
        "range": [0, 10],
        "description": "Apply smoothing to skeletons (kernel size)"
      },
      "min_branch_length": {
        "default": 5,
        "value": 5,
        "name": "Minimum Branch Length",
        "range": [3, 30],
        "description": "Ignore branches shorter than this (pixels)"
      },
      "curvature_tolerance": {
        "default": 0.7,
        "value": 0.7,
        "name": "Curvature Tolerance",
        "range": [0.1, 0.9],
        "description": "Similarity threshold for curved fiber segments (0-1)"
      }
    }
  },
  "intensity_parameters": {
    "name": "Intensity Parameters",
    "parameters": {
      "intensity_threshold": {
        "default": 0.75,
        "value": 0.75,
        "name": "Intensity Threshold",
        "range": [0.1, 1.0],
        "description": "Intensity similarity threshold (0-1)"
      },
      "intensity_gradient_max": {
        "default": 20,
        "value": 20,
        "name": "Intensity Gradient Max",
        "range": [5, 50],
        "description": "Maximum allowed intensity change along a fiber"
      },
      "fiber_contrast_min": {
        "default": 15,
        "value": 15,
        "name": "Fiber Contrast Min",
        "range": [5, 50],
        "description": "Minimum contrast between fiber and background"
      },
      "enhance_contrast": {
        "default": true,
        "value": true,
        "name": "Enhance Contrast",
        "range": [true, false],
        "description": "Apply contrast enhancement to better detect fibers"
      }
    }
  },
  "weights_and_iterations": {
    "name": "Weights and Iterations",
    "parameters": {
      "distance_weight": {
        "default": 0.4,
        "value": 0.4,
        "name": "Distance Weight",
        "range": [0.0, 1.0],
        "description": "Weight for distance between fiber endpoints in fiber joining"
      },
      "angle_weight": {
        "default": 0.3,
        "value": 0.3,
        "name": "Angle Weight",
        "range": [0.0, 1.0],
        "description": "Weight for angle between fiber segments in fiber joining"
      },
      "intensity_weight": {
        "default": 0.2,
        "value": 0.2,
        "name": "Intensity Weight",
        "range": [0.0, 1.0],
        "description": "Weight for intensity similarity in fiber joining"
      },
      "curvature_weight": {
        "default": 0.1,
        "value": 0.1,
        "name": "Curvature Weight",
        "range": [0.0, 1.0],
        "description": "Weight for curvature similarity in fiber joining"
      },
      "junction_distance_weight": {
        "default": 0.6,
        "value": 0.6,
        "name": "Junction Distance Weight",
        "range": [0.0, 1.0],
        "description": "Weight for distance between junction points in fiber joining"
      },
      "junction_intensity_weight": {
        "default": 0.4,
        "value": 0.4,
        "name": "Junction Intensity Weight",
        "range": [0.0, 1.0],
        "description": "Weight for intensity similarity at junction points in fiber joining"
      },
      "iterations": {
        "default": 1,
        "value": 1,
        "name": "Iterations",
        "range": [1, 10],
        "description": "Number of iterations for fiber joining and junction detection"
      }
    }
  }
}

export type SemAnalysisState = {
  semAnalysisData: any;
  semAnalysisDataStatus: AsyncStates;
  semAnalysisDataError: String;

  classificationAnalysis: any;
  classificationAnalysisStatus: AsyncStates;
  classificationAnalysisError: String;

  semAnalysisMaskData: any;
  semAnalysisMaskDataStatus: AsyncStates;
  semAnalysisMaskDataError: String;

  semAnalysisList: any;
  semAnalysisListStatus: AsyncStates;
  semAnalysisListError: String;

  semAnalysisScale: any;
  semAnalysisScaleStatus: AsyncStates;
  semAnalysisScaleError: String;

  deleteSemAnalysisFiles: any;
  deleteSemAnalysisFilesStatus: AsyncStates;
  deleteSemAnalysisFilesError: String;

  imageSubtraction: any;
  imageSubtractionStatus: AsyncStates;
  imageSubtractionError: String;

  setImageSubtraction: any;
  setImageSubtractionStatus: AsyncStates;
  setImageSubtractionError: String;

  instanceDetection: any;
  instanceDetectionStatus: AsyncStates;
  instanceDetectionError: String;

  setSemAnalysisMaskHeightWidth: any;
  setSemAnalysisMaskHeightWidthStatus: AsyncStates;
  setSemAnalysisMaskHeightWidthError: String;

  semAnalysisMaskHeightWidth: any;
  semAnalysisMaskHeightWidthStatus: AsyncStates;
  semAnalysisMaskHeightWidthError: String;

  deleteSemAnalysisMaskHeightWidth: any;
  deleteSemAnalysisMaskHeightWidthStatus: AsyncStates;
  deleteSemAnalysisMaskHeightWidthError: String;

  downloadSemAnalysisReport: string;
  downloadSemAnalysisReportStatus: AsyncStates;
  downloadSemAnalysisReportError: String;

  deleteMasks: any;
  deleteMasksStatus: AsyncStates;
  deleteMasksError: String;

  updateClassificationStatus: AsyncStates;
  updateClassificationError: String;

  resetMasks: any,
  resetMasksStatus: AsyncStates,
  resetMasksError: string,

  processPaintFilmAnalysis: any,
  processPaintFilmAnalysisStatus: AsyncStates,
  processPaintFilmAnalysisError: string,

  paintFilmBatchInfo: any[],
  paintFilmBatchInfoStatus: AsyncStates,
  paintFilmBatchInfoError: string,

  downloadBatchReport: string,
  downloadBatchReportStatus: AsyncStates,
  downloadBatchReportError: String,

  deletePaintFilmBatch: any;
  deletePaintFilmBatchStatus: AsyncStates;
  deletePaintFilmBatchError: String;

  paintFilmRecalibrateScaleStatus: AsyncStates;
  paintFilmRecalibrateScaleError: String;

  analysisTypes: {};
  analysisTypesStatus: AsyncStates;
  analysisTypesError: String;

  residualBatchInfo: any[],
  residualBatchInfoStatus: AsyncStates,
  residualBatchInfoError: string,

  deleteResidualBatch: any;
  deleteResidualBatchStatus: AsyncStates;
  deleteResidualBatchError: String;

  residualRecalibrateScaleStatus: AsyncStates;
  residualRecalibrateScaleError: String;

  modifyPaintFilmStatus: AsyncStates;

  modifyPaintFilmBatchStatus: AsyncStates;
  modifyPaintFilmBatchData: any;
  modifyPaintFilmBatchError: '';

  processFiberAnalysis: any,
  processFiberAnalysisStatus: AsyncStates,
  processFiberAnalysisError: String,

  fiberBatchInfo: any[],
  fiberBatchInfoStatus: AsyncStates,
  fiberBatchInfoError: string,

  deleteFiberBatch: {},
  deleteFiberBatchStatus: AsyncStates,
  deleteFiberBatchError: String,

  fiberRecalibrateScaleStatus: AsyncStates;
  fiberRecalibrateScaleError: String;

  processWhiteObjects: {},
  processWhiteObjectsStatus: AsyncStates,
  processWhiteObjectsError: String,

  modifyWhiteObjects: {},
  modifyWhiteObjectsStatus: AsyncStates,
  modifyWhiteObjectsError: String,

  modifyFiberStatus: AsyncStates;

  downloadSegmentedImagesStatus: AsyncStates,
  fiberAnalysisCurrentPhase: {
    [key: string]: string
  } | null,
  phaseTwoFiberAnalysisData: {
    [key: string]: {
      status: "processing" | "completed" | "failed",
      message: string,
      data: any,
      progress: number | null,
      step: number | null
    }
  } | null

  processBlackObjectsAnalysis: any,
  processBlackObjectsAnalysisStatus: AsyncStates,
  processBlackObjectsAnalysisError: String,

  blackObjectsBatchInfo: any[],
  blackObjectsBatchInfoStatus: AsyncStates,
  blackObjectsBatchInfoError: string,

  deleteBlackObjectsBatch: {},
  deleteBlackObjectsBatchStatus: AsyncStates,
  deleteBlackObjectsBatchError: String,

  blackObjectsRecalibrateScaleStatus: AsyncStates;
  blackObjectsRecalibrateScaleError: String;

  modifyBlackObjectsStatus: AsyncStates,
  fiberAnalysisPhaseTwoReportDownloadStatus: AsyncStates,

  fiberAnalysisPhaseTwoParameters: any,
  fiberAnalysisPhaseTwoParametersStatus: AsyncStates
};

const defaultState: SemAnalysisState = {
  semAnalysisData: {},
  semAnalysisDataStatus: AsyncStates.INITIAL,
  semAnalysisDataError: "",

  classificationAnalysis: {},
  classificationAnalysisStatus: AsyncStates.INITIAL,
  classificationAnalysisError: "",

  semAnalysisMaskData: {},
  semAnalysisMaskDataStatus: AsyncStates.INITIAL,
  semAnalysisMaskDataError: "",

  semAnalysisList: [],
  semAnalysisListStatus: AsyncStates.INITIAL,
  semAnalysisListError: "",

  semAnalysisScale: {},
  semAnalysisScaleStatus: AsyncStates.INITIAL,
  semAnalysisScaleError: "",

  deleteSemAnalysisFiles: {},
  deleteSemAnalysisFilesStatus: AsyncStates.INITIAL,
  deleteSemAnalysisFilesError: "",

  imageSubtraction: {},
  imageSubtractionStatus: AsyncStates.INITIAL,
  imageSubtractionError: "",

  setImageSubtraction: {},
  setImageSubtractionStatus: AsyncStates.INITIAL,
  setImageSubtractionError: "",

  instanceDetection: {},
  instanceDetectionStatus: AsyncStates.INITIAL,
  instanceDetectionError: "",

  setSemAnalysisMaskHeightWidth: {},
  setSemAnalysisMaskHeightWidthStatus: AsyncStates.INITIAL,
  setSemAnalysisMaskHeightWidthError: "",

  semAnalysisMaskHeightWidth: {},
  semAnalysisMaskHeightWidthStatus: AsyncStates.INITIAL,
  semAnalysisMaskHeightWidthError: "",

  deleteSemAnalysisMaskHeightWidth: '',
  deleteSemAnalysisMaskHeightWidthStatus: AsyncStates.INITIAL,
  deleteSemAnalysisMaskHeightWidthError: '',

  downloadSemAnalysisReport: '',
  downloadSemAnalysisReportStatus: AsyncStates.INITIAL,
  downloadSemAnalysisReportError: '',

  deleteMasks: '',
  deleteMasksStatus: AsyncStates.INITIAL,
  deleteMasksError: '',

  updateClassificationStatus: AsyncStates.INITIAL,
  updateClassificationError: '',

  resetMasks: '',
  resetMasksStatus: AsyncStates.INITIAL,
  resetMasksError: '',

  processPaintFilmAnalysis: {},
  processPaintFilmAnalysisStatus: AsyncStates.INITIAL,
  processPaintFilmAnalysisError: '',

  paintFilmBatchInfo: [],
  paintFilmBatchInfoStatus: AsyncStates.INITIAL,
  paintFilmBatchInfoError: '',

  downloadBatchReport: '',
  downloadBatchReportStatus: AsyncStates.INITIAL,
  downloadBatchReportError: '',

  deletePaintFilmBatch: {},
  deletePaintFilmBatchStatus: AsyncStates.INITIAL,
  deletePaintFilmBatchError: '',

  paintFilmRecalibrateScaleStatus: AsyncStates.INITIAL,
  paintFilmRecalibrateScaleError: '',

  analysisTypes: {},
  analysisTypesStatus: AsyncStates.INITIAL,
  analysisTypesError: '',

  residualBatchInfo: [],
  residualBatchInfoStatus: AsyncStates.INITIAL,
  residualBatchInfoError: '',

  deleteResidualBatch: {},
  deleteResidualBatchStatus: AsyncStates.INITIAL,
  deleteResidualBatchError: '',

  residualRecalibrateScaleStatus: AsyncStates.INITIAL,
  residualRecalibrateScaleError: '',

  modifyPaintFilmStatus: AsyncStates.INITIAL,

  modifyPaintFilmBatchStatus: AsyncStates.INITIAL,
  modifyPaintFilmBatchData: {},
  modifyPaintFilmBatchError: '',

  processFiberAnalysis: {},
  processFiberAnalysisStatus: AsyncStates.INITIAL,
  processFiberAnalysisError: '',

  fiberBatchInfo: [],
  fiberBatchInfoStatus: AsyncStates.INITIAL,
  fiberBatchInfoError: '',

  deleteFiberBatch: {},
  deleteFiberBatchStatus: AsyncStates.INITIAL,
  deleteFiberBatchError: '',

  fiberRecalibrateScaleStatus: AsyncStates.INITIAL,
  fiberRecalibrateScaleError: '',

  processWhiteObjects: {},
  processWhiteObjectsStatus: AsyncStates.INITIAL,
  processWhiteObjectsError: '',

  modifyWhiteObjects: {},
  modifyWhiteObjectsStatus: AsyncStates.INITIAL,
  modifyWhiteObjectsError: '',

  modifyFiberStatus: AsyncStates.INITIAL,

  downloadSegmentedImagesStatus: AsyncStates.INITIAL,
  fiberAnalysisCurrentPhase: null,
  phaseTwoFiberAnalysisData: null,

  processBlackObjectsAnalysis: {},
  processBlackObjectsAnalysisStatus: AsyncStates.INITIAL,
  processBlackObjectsAnalysisError: "",

  blackObjectsBatchInfo: [],
  blackObjectsBatchInfoStatus: AsyncStates.INITIAL,
  blackObjectsBatchInfoError: "",

  deleteBlackObjectsBatch: {},
  deleteBlackObjectsBatchStatus: AsyncStates.INITIAL,
  deleteBlackObjectsBatchError: "",

  blackObjectsRecalibrateScaleStatus: AsyncStates.INITIAL,
  blackObjectsRecalibrateScaleError: "",

  modifyBlackObjectsStatus: AsyncStates.INITIAL,
  fiberAnalysisPhaseTwoReportDownloadStatus: AsyncStates.INITIAL,

  fiberAnalysisPhaseTwoParameters: PhaseTwoDefaultParameters,
  fiberAnalysisPhaseTwoParametersStatus: AsyncStates.INITIAL
};

const semAnalysisReducer = handleActions<SemAnalysisState, any>(
  {
    [FetchSemAnalysisDataActionTypes.REQUEST]: (state) => ({
      ...state,
      semAnalysisDataStatus: AsyncStates.LOADING,
    }),
    [FetchSemAnalysisDataActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      semAnalysisDataStatus: AsyncStates.SUCCESS,
      semAnalysisData: action.payload ?? {},
    }),
    [FetchSemAnalysisDataActionTypes.FAILURE]: (state, action) => ({
      ...state,
      semAnalysisDataStatus: AsyncStates.ERROR,
      semAnalysisDataError: action.payload,
    }),
    [FetchSemAnalysisDataActionTypes.CLEAR]: (state, action) => ({
      ...state,
      semAnalysisDataStatus: AsyncStates.INITIAL,
    }),

    [FetchClassificationAnalysisActionTypes.REQUEST]: (state) => ({
      ...state,
      classificationAnalysisStatus: AsyncStates.LOADING,
    }),
    [FetchClassificationAnalysisActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      classificationAnalysisStatus: AsyncStates.SUCCESS,
      classificationAnalysis: action.payload ?? {},
    }),
    [FetchClassificationAnalysisActionTypes.FAILURE]: (state, action) => ({
      ...state,
      classificationAnalysisStatus: AsyncStates.ERROR,
      classificationAnalysisError: action.payload,
    }),
    [FetchClassificationAnalysisActionTypes.CLEAR]: (state, action) => ({
      ...state,
      classificationAnalysisStatus: AsyncStates.INITIAL
    }),

    [FetchSemAnalysisMaskDataActionTypes.REQUEST]: (state) => ({
      ...state,
      semAnalysisMaskDataStatus: AsyncStates.LOADING,
      semAnalysisMaskDataError: '',
    }),
    [FetchSemAnalysisMaskDataActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      semAnalysisMaskDataStatus: AsyncStates.SUCCESS,
      semAnalysisMaskData: action.payload ?? {},
    }),
    [FetchSemAnalysisMaskDataActionTypes.FAILURE]: (state, action) => ({
      ...state,
      semAnalysisMaskDataStatus: AsyncStates.ERROR,
      semAnalysisMaskDataError: action.payload,
    }),
    [FetchSemAnalysisMaskDataActionTypes.CLEAR]: (state, action) => ({
      ...state,
      semAnalysisMaskData: {},
      semAnalysisMaskDataStatus: AsyncStates.INITIAL,
      semAnalysisMaskDataError: '',
    }),

    [FetchSemAnalysisListActionTypes.REQUEST]: (state) => ({
      ...state,
      semAnalysisListStatus: AsyncStates.LOADING,
    }),
    [FetchSemAnalysisListActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      semAnalysisListStatus: AsyncStates.SUCCESS,
      semAnalysisList: action.payload ?? {},
    }),
    [FetchSemAnalysisListActionTypes.FAILURE]: (state, action) => ({
      ...state,
      semAnalysisListStatus: AsyncStates.ERROR,
      semAnalysisListError: action.payload,
    }),
    [FetchSemAnalysisListActionTypes.CLEAR]: (state, action) => ({
      ...state,
      semAnalysisList: [],
      semAnalysisListStatus: AsyncStates.INITIAL,
      semAnalysisListError: '',
    }),

    [FetchSemAnalysisScaleActionTypes.REQUEST]: (state) => ({
      ...state,
      semAnalysisScaleStatus: AsyncStates.LOADING,
    }),
    [FetchSemAnalysisScaleActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      semAnalysisScaleStatus: AsyncStates.SUCCESS,
      semAnalysisScale: action.payload ?? {},
    }),
    [FetchSemAnalysisScaleActionTypes.FAILURE]: (state, action) => ({
      ...state,
      semAnalysisScaleStatus: AsyncStates.ERROR,
      semAnalysisScaleError: action.payload,
    }),

    [DeleteSemAnalysisFilesActionTypes.REQUEST]: (state) => ({
      ...state,
      deleteSemAnalysisFilesStatus: AsyncStates.LOADING,
    }),
    [DeleteSemAnalysisFilesActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deleteSemAnalysisFilesStatus: AsyncStates.SUCCESS,
      deleteSemAnalysisFiles: action.payload ?? {},
    }),
    [DeleteSemAnalysisFilesActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deleteSemAnalysisFilesStatus: AsyncStates.ERROR,
      deleteSemAnalysisFilesError: action.payload,
    }),
    [DeleteSemAnalysisFilesActionTypes.CLEAR]: (state, action) => ({
      ...state,
      deleteSemAnalysisFilesStatus: AsyncStates.INITIAL,
    }),

    [FetchImageSubtractionActionTypes.REQUEST]: (state) => ({
      ...state,
      imageSubtractionStatus: AsyncStates.LOADING,
    }),
    [FetchImageSubtractionActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      imageSubtractionStatus: AsyncStates.SUCCESS,
      imageSubtraction: action.payload ?? {},
    }),
    [FetchImageSubtractionActionTypes.FAILURE]: (state, action) => ({
      ...state,
      imageSubtractionStatus: AsyncStates.ERROR,
      imageSubtractionError: action.payload,
    }),

    [SetImageSubtractionActionTypes.REQUEST]: (state) => ({
      ...state,
      setImageSubtractionStatus: AsyncStates.LOADING,
    }),
    [SetImageSubtractionActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      setImageSubtractionStatus: AsyncStates.SUCCESS,
      setImageSubtraction: action.payload ?? {},
    }),
    [SetImageSubtractionActionTypes.FAILURE]: (state, action) => ({
      ...state,
      setImageSubtractionStatus: AsyncStates.ERROR,
      setImageSubtractionError: action.payload,
    }),
    [SetImageSubtractionActionTypes.CLEAR]: (state, action) => ({
      ...state,
      setImageSubtractionStatus: AsyncStates.INITIAL,
    }),

    [FetchInstanceDetectionActionTypes.REQUEST]: (state) => ({
      ...state,
      instanceDetectionStatus: AsyncStates.LOADING,
    }),
    [FetchInstanceDetectionActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      instanceDetectionStatus: AsyncStates.SUCCESS,
      instanceDetection: action.payload ?? {},
    }),
    [FetchInstanceDetectionActionTypes.FAILURE]: (state, action) => ({
      ...state,
      instanceDetectionStatus: AsyncStates.ERROR,
      instanceDetectionError: action.payload,
    }),
    [FetchInstanceDetectionActionTypes.CLEAR]: (state) => ({
      ...state,
      instanceDetectionStatus: AsyncStates.INITIAL,
      instanceDetectionError: "",
      instanceDetection: {},
    }),

    [SetSemAnalysisMaskHeightWidthActionTypes.REQUEST]: (state) => ({
      ...state,
      setSemAnalysisMaskHeightWidthStatus: AsyncStates.LOADING,
    }),
    [SetSemAnalysisMaskHeightWidthActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      setSemAnalysisMaskHeightWidthStatus: AsyncStates.SUCCESS,
      setSemAnalysisMaskHeightWidth: action.payload ?? {},
    }),
    [SetSemAnalysisMaskHeightWidthActionTypes.FAILURE]: (state, action) => ({
      ...state,
      setSemAnalysisMaskHeightWidthStatus: AsyncStates.ERROR,
      setSemAnalysisMaskHeightWidthError: action.payload,
    }),

    [FetchSemAnalysisMaskHeightWidthActionTypes.REQUEST]: (state) => ({
      ...state,
      semAnalysisMaskHeightWidthStatus: AsyncStates.LOADING,
    }),
    [FetchSemAnalysisMaskHeightWidthActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      semAnalysisMaskHeightWidthStatus: AsyncStates.SUCCESS,
      semAnalysisMaskHeightWidth: action.payload ?? {},
    }),
    [FetchSemAnalysisMaskHeightWidthActionTypes.FAILURE]: (state, action) => ({
      ...state,
      semAnalysisMaskHeightWidthStatus: AsyncStates.ERROR,
      semAnalysisMaskHeightWidthError: action.payload,
    }),
    [FetchSemAnalysisMaskHeightWidthActionTypes.CLEAR]: (state) => ({
      ...state,
      semAnalysisMaskHeightWidthStatus: AsyncStates.INITIAL,
      semAnalysisMaskHeightWidthError: "",
      semAnalysisMaskHeightWidth: {},
    }),
    [DeleteSemAnalysisMaskHeightWidthActionTypes.REQUEST]: (state) => ({
      ...state,
      deleteSemAnalysisMaskHeightWidthStatus: AsyncStates.LOADING,
    }),
    [DeleteSemAnalysisMaskHeightWidthActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deleteSemAnalysisMaskHeightWidthStatus: AsyncStates.SUCCESS,
      deleteSemAnalysisMaskHeightWidth: action.payload ?? '',
    }),
    [DeleteSemAnalysisMaskHeightWidthActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deleteSemAnalysisMaskHeightWidthStatus: AsyncStates.ERROR,
      deleteSemAnalysisMaskHeightWidthError: action.payload,
    }),

    [DownloadSemAnalysisReportActionTypes.REQUEST]: (state) => ({
      ...state,
      downloadSemAnalysisReportStatus: AsyncStates.LOADING,
    }),
    [DownloadSemAnalysisReportActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      downloadSemAnalysisReportStatus: AsyncStates.SUCCESS,
      downloadSemAnalysisReport: action.payload ?? '',
    }),
    [DownloadSemAnalysisReportActionTypes.FAILURE]: (state, action) => ({
      ...state,
      downloadSemAnalysisReportStatus: AsyncStates.ERROR,
      downloadSemAnalysisReportError: action.payload,
    }),

    [DeleteMasksActionTypes.REQUEST]: (state) => ({
      ...state,
      deleteMasksStatus: AsyncStates.LOADING,
    }),
    [DeleteMasksActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deleteMasksStatus: AsyncStates.SUCCESS,
      deleteMasksError: action.payload ?? '',
    }),
    [DeleteMasksActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deleteMasksStatus: AsyncStates.ERROR,
      deleteMasksError: action.payload,
    }),

    [UpdateClassificationActionTypes.REQUEST]: (state) => ({
      ...state,
      updateClassificationStatus: AsyncStates.LOADING,
    }),
    [UpdateClassificationActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      updateClassificationStatus: AsyncStates.SUCCESS,
      classificationAnalysis: action.payload ?? {},
    }),
    [UpdateClassificationActionTypes.FAILURE]: (state, action) => ({
      ...state,
      updateClassificationStatus: AsyncStates.ERROR,
      updateClassificationError: action.payload,
    }),

    [ResetMasksActionTypes.REQUEST]: (state) => ({
      ...state,
      resetMasksStatus: AsyncStates.LOADING,
    }),
    [ResetMasksActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      resetMasksStatus: AsyncStates.SUCCESS,
      resetMasksError: action.payload ?? '',
    }),
    [ResetMasksActionTypes.FAILURE]: (state, action) => ({
      ...state,
      resetMasksStatus: AsyncStates.ERROR,
      resetMasksError: action.payload,
    }),

    [ProcessPaintFilmAnalysisActionTypes.REQUEST]: (state) => ({
      ...state,
      processPaintFilmAnalysisStatus: AsyncStates.LOADING,
    }),
    [ProcessPaintFilmAnalysisActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      processPaintFilmAnalysis: action.payload ?? {},
      processPaintFilmAnalysisStatus: AsyncStates.SUCCESS,
    }),
    [ProcessPaintFilmAnalysisActionTypes.FAILURE]: (state, action) => ({
      ...state,
      processPaintFilmAnalysisStatus: AsyncStates.ERROR,
      processPaintFilmAnalysisError: action.payload,
    }),
    [ProcessPaintFilmAnalysisActionTypes.CLEAR]: (state, action) => ({
      ...state,
      processPaintFilmAnalysisStatus: AsyncStates.INITIAL,
    }),

    [FetchPaintFilmBatchInfoActionTypes.REQUEST]: (state) => ({
      ...state,
      paintFilmBatchInfoStatus: AsyncStates.LOADING,
    }),
    [FetchPaintFilmBatchInfoActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      paintFilmBatchInfoStatus: AsyncStates.SUCCESS,
      paintFilmBatchInfo: action.payload ?? []
    }),
    [FetchPaintFilmBatchInfoActionTypes.FAILURE]: (state, action) => ({
      ...state,
      paintFilmBatchInfoStatus: AsyncStates.ERROR,
      paintFilmBatchInfoError: action.payload,
    }),
    [FetchPaintFilmBatchInfoActionTypes.CLEAR]: (state) => ({
      ...state,
      paintFilmBatchInfo: [],
      paintFilmBatchInfoStatus: AsyncStates.INITIAL,
      paintFilmBatchInfoError: "",
    }),

    [DownloadBatchReportActionTypes.REQUEST]: (state) => ({
      ...state,
      downloadBatchReportStatus: AsyncStates.LOADING,
    }),
    [DownloadBatchReportActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      downloadBatchReportStatus: AsyncStates.SUCCESS,
      downloadBatchReport: action.payload ?? '',
    }),
    [DownloadBatchReportActionTypes.FAILURE]: (state, action) => ({
      ...state,
      downloadBatchReportStatus: AsyncStates.ERROR,
      downloadBatchReportError: action.payload,
    }),

    [DeletePaintFilmBatchActionTypes.REQUEST]: (state) => ({
      ...state,
      deletePaintFilmBatchStatus: AsyncStates.LOADING,
    }),
    [DeletePaintFilmBatchActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deletePaintFilmBatchStatus: AsyncStates.SUCCESS,
      deletePaintFilmBatch: action.payload ?? {},
    }),
    [DeletePaintFilmBatchActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deletePaintFilmBatchStatus: AsyncStates.ERROR,
      deletePaintFilmBatchError: action.payload,
    }),
    [DeletePaintFilmBatchActionTypes.CLEAR]: (state, action) => ({
      ...state,
      deletePaintFilmBatchStatus: AsyncStates.INITIAL,
    }),

    [PaintFilmRecalibrateScaleActionTypes.REQUEST]: (state) => ({
      ...state,
      paintFilmRecalibrateScaleStatus: AsyncStates.LOADING,
    }),
    [PaintFilmRecalibrateScaleActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      paintFilmRecalibrateScaleStatus: AsyncStates.SUCCESS
    }),
    [PaintFilmRecalibrateScaleActionTypes.FAILURE]: (state, action) => ({
      ...state,
      paintFilmRecalibrateScaleStatus: AsyncStates.ERROR,
      paintFilmRecalibrateScaleError: action.payload,
    }),
    [PaintFilmRecalibrateScaleActionTypes.CLEAR]: (state, action) => ({
      ...state,
      paintFilmRecalibrateScaleStatus: AsyncStates.INITIAL
    }),

    [AnalysisTypesActionTypes.REQUEST]: (state) => ({
      ...state,
      analysisTypesStatus: AsyncStates.LOADING,
    }),
    [AnalysisTypesActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      analysisTypesStatus: AsyncStates.SUCCESS,
      analysisTypes: action.payload ?? {},
    }),
    [AnalysisTypesActionTypes.FAILURE]: (state, action) => ({
      ...state,
      analysisTypesStatus: AsyncStates.ERROR,
      analysisTypesError: action.payload,
    }),

    [FetchResidualBatchInfoActionTypes.REQUEST]: (state) => ({
      ...state,
      residualBatchInfoStatus: AsyncStates.LOADING,
    }),
    [FetchResidualBatchInfoActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      residualBatchInfoStatus: AsyncStates.SUCCESS,
      residualBatchInfo: action.payload ?? []
    }),
    [FetchResidualBatchInfoActionTypes.FAILURE]: (state, action) => ({
      ...state,
      residualBatchInfoStatus: AsyncStates.ERROR,
      residualBatchInfoError: action.payload,
    }),
    [FetchResidualBatchInfoActionTypes.CLEAR]: (state) => ({
      ...state,
      residualBatchInfo: [],
      residualBatchInfoStatus: AsyncStates.INITIAL,
      residualBatchInfoError: "",
    }),

    [DeleteResidualBatchActionTypes.REQUEST]: (state) => ({
      ...state,
      deleteResidualBatchStatus: AsyncStates.LOADING,
    }),
    [DeleteResidualBatchActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deleteResidualBatchStatus: AsyncStates.SUCCESS,
      deleteResidualBatch: action.payload ?? {},
    }),
    [DeleteResidualBatchActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deleteResidualBatchStatus: AsyncStates.ERROR,
      deleteResidualBatchError: action.payload,
    }),
    [DeleteResidualBatchActionTypes.CLEAR]: (state, action) => ({
      ...state,
      deleteResidualBatchStatus: AsyncStates.INITIAL,
    }),

    [ResidualRecalibrateScaleActionTypes.REQUEST]: (state) => ({
      ...state,
      residualRecalibrateScaleStatus: AsyncStates.LOADING,
    }),
    [ResidualRecalibrateScaleActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      residualRecalibrateScaleStatus: AsyncStates.SUCCESS
    }),
    [ResidualRecalibrateScaleActionTypes.FAILURE]: (state, action) => ({
      ...state,
      residualRecalibrateScaleStatus: AsyncStates.ERROR,
      residualRecalibrateScaleError: action.payload,
    }),
    [ResidualRecalibrateScaleActionTypes.CLEAR]: (state, action) => ({
      ...state,
      residualRecalibrateScaleStatus: AsyncStates.INITIAL
    }),

    [ModifyPaintFilmActionTypes.REQUEST]: (state) => ({
      ...state,
      modifyPaintFilmStatus: AsyncStates.LOADING,
    }),
    [ModifyPaintFilmActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      modifyPaintFilmStatus: AsyncStates.SUCCESS
    }),
    [ModifyPaintFilmActionTypes.FAILURE]: (state, action) => ({
      ...state,
      modifyPaintFilmStatus: AsyncStates.ERROR
    }),

    [ModifyPaintFilmBatchActionTypes.REQUEST]: (state) => ({
      ...state,
      modifyPaintFilmBatchStatus: AsyncStates.LOADING,
    }),
    [ModifyPaintFilmBatchActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      modifyPaintFilmBatchStatus: AsyncStates.SUCCESS,
      modifyPaintFilmBatchData: action.payload
    }),
    [ModifyPaintFilmBatchActionTypes.FAILURE]: (state, action) => ({
      ...state,
      modifyPaintFilmBatchStatus: AsyncStates.ERROR
    }),
    [ModifyPaintFilmBatchActionTypes.CLEAR]: (state, action) => ({
      ...state,
      modifyPaintFilmBatchStatus: AsyncStates.INITIAL,
      modifyPaintFilmBatchData: {}
    }),

    [ProcessFiberAnalysisActionTypes.REQUEST]: (state) => ({
      ...state,
      processFiberAnalysisStatus: AsyncStates.LOADING,
    }),
    [ProcessFiberAnalysisActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      processFiberAnalysis: action.payload ?? {},
      processFiberAnalysisStatus: AsyncStates.SUCCESS,
    }),
    [ProcessFiberAnalysisActionTypes.FAILURE]: (state, action) => ({
      ...state,
      processFiberAnalysisStatus: AsyncStates.ERROR,
      processFiberAnalysisError: action.payload,
    }),
    [ProcessFiberAnalysisActionTypes.CLEAR]: (state, action) => ({
      ...state,
      processFiberAnalysisStatus: AsyncStates.INITIAL,
    }),

    [FetchFiberBatchInfoActionTypes.REQUEST]: (state) => ({
      ...state,
      fiberBatchInfoStatus: AsyncStates.LOADING,
    }),
    [FetchFiberBatchInfoActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      fiberBatchInfoStatus: AsyncStates.SUCCESS,
      fiberBatchInfo: action.payload ?? []
    }),
    [FetchFiberBatchInfoActionTypes.FAILURE]: (state, action) => ({
      ...state,
      fiberBatchInfoStatus: AsyncStates.ERROR,
      fiberBatchInfoError: action.payload,
    }),
    [FetchFiberBatchInfoActionTypes.CLEAR]: (state) => ({
      ...state,
      fiberBatchInfo: [],
      fiberBatchInfoStatus: AsyncStates.INITIAL,
      fiberBatchInfoError: "",
    }),

    [DeleteFiberBatchActionTypes.REQUEST]: (state) => ({
      ...state,
      deleteFiberBatchStatus: AsyncStates.LOADING,
    }),
    [DeleteFiberBatchActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deleteFiberBatchStatus: AsyncStates.SUCCESS,
      deleteFiberBatch: action.payload ?? {},
    }),
    [DeleteFiberBatchActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deleteFiberBatchStatus: AsyncStates.ERROR,
      deleteFiberBatchError: action.payload,
    }),
    [DeleteFiberBatchActionTypes.CLEAR]: (state, action) => ({
      ...state,
      deleteFiberBatchStatus: AsyncStates.INITIAL,
    }),

    [FiberRecalibrateScaleActionTypes.REQUEST]: (state) => ({
      ...state,
      fiberRecalibrateScaleStatus: AsyncStates.LOADING,
    }),
    [FiberRecalibrateScaleActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      fiberRecalibrateScaleStatus: AsyncStates.SUCCESS
    }),
    [FiberRecalibrateScaleActionTypes.FAILURE]: (state, action) => ({
      ...state,
      fiberRecalibrateScaleStatus: AsyncStates.ERROR,
      fiberRecalibrateScaleError: action.payload,
    }),
    [FiberRecalibrateScaleActionTypes.CLEAR]: (state, action) => ({
      ...state,
      fiberRecalibrateScaleStatus: AsyncStates.INITIAL
    }),

    [ProcessWhiteObjectsActionTypes.REQUEST]: (state) => ({
      ...state,
      processWhiteObjectsStatus: AsyncStates.LOADING,
    }),
    [ProcessWhiteObjectsActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      processWhiteObjects: action.payload ?? {},
      processWhiteObjectsStatus: AsyncStates.SUCCESS,
    }),
    [ProcessWhiteObjectsActionTypes.FAILURE]: (state, action) => ({
      ...state,
      processWhiteObjectsStatus: AsyncStates.ERROR,
      processWhiteObjectsError: action.payload,
    }),
    [ProcessWhiteObjectsActionTypes.CLEAR]: (state, action) => ({
      ...state,
      processWhiteObjectsStatus: AsyncStates.INITIAL,
    }),

    [ModifyWhiteObjectsActionTypes.REQUEST]: (state) => ({
      ...state,
      modifyWhiteObjectsStatus: AsyncStates.LOADING,
    }),
    [ModifyWhiteObjectsActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      modifyWhiteObjects: action.payload ?? {},
      modifyWhiteObjectsStatus: AsyncStates.SUCCESS,
    }),
    [ModifyWhiteObjectsActionTypes.FAILURE]: (state, action) => ({
      ...state,
      modifyWhiteObjectsStatus: AsyncStates.ERROR,
      modifyWhiteObjectsError: action.payload,
    }),
    [ModifyWhiteObjectsActionTypes.CLEAR]: (state, action) => ({
      ...state,
      modifyWhiteObjectsStatus: AsyncStates.INITIAL,
    }),

    [ModifyFiberActionTypes.REQUEST]: (state) => ({
      ...state,
      modifyFiberStatus: AsyncStates.LOADING,
    }),
    [ModifyFiberActionTypes.SUCCESS]: (state, action) => {
      return {
        ...state,
        modifyFiberStatus: AsyncStates.SUCCESS,
      }
    },
    [ModifyFiberActionTypes.FAILURE]: (state, action) => ({
      ...state,
      modifyFiberStatus: AsyncStates.ERROR
    }),

    [DownloadSegmentedImagesTypes.REQUEST]: (state) => ({
      ...state,
      downloadSegmentedImagesStatus: AsyncStates.LOADING,
    }),
    [DownloadSegmentedImagesTypes.SUCCESS]: (state, action) => ({
      ...state,
      downloadSegmentedImagesStatus: AsyncStates.SUCCESS
    }),
    [DownloadSegmentedImagesTypes.FAILURE]: (state, action) => ({
      ...state,
      downloadSegmentedImagesStatus: AsyncStates.ERROR
    }),

    [PhaseTwoFiberAnalysisActionTypes.REQUEST]: (state, action) => ({
      ...state,
      phaseTwoFiberAnalysisData: {
        ...state.phaseTwoFiberAnalysisData,
        [action.payload?.file_id]: {
          ...(state.phaseTwoFiberAnalysisData?.[action.payload?.file_id] ?? {}),
          status: action.payload?.command === "start" ? "processing" : null,
          message: null,
          step: action.payload?.command === "start" ? "Initializing" : null,
          progress: 0,
        }
      }
    }),
    [PhaseTwoFiberAnalysisActionTypes.SUCCESS]: (state, action) => {
      return {
        ...state,
        phaseTwoFiberAnalysisData: {
          ...state.phaseTwoFiberAnalysisData,
          [action.payload?.file_id]: {
            status: action.payload?.status,
            message: action.payload?.message,
            step: action.payload?.step,
            progress: action.payload?.progress,
            data: action.payload?.data
          }
        }
      }
    },
    [PhaseTwoFiberAnalysisActionTypes.FAILURE]: (state, action) => ({
      ...state,
      phaseTwoFiberAnalysisData: {
        ...state.phaseTwoFiberAnalysisData,
        [action.payload?.file_id]: {
          status: action.payload?.status,
          message: action.payload?.message,
          step: action.payload?.step,
          progress: action.payload?.progress,
          data: action.payload?.result
        }
      }
    }),
    [PhaseTwoFiberAnalysisActionTypes.CLEAR]: (state, action) => {
      return {
        ...state,
        phaseTwoFiberAnalysisData: {
          ...state.phaseTwoFiberAnalysisData,
          [action.payload?.file_id]: {
            status: action.payload?.status,
            message: action.payload?.message,
            step: action.payload?.step,
            progress: action.payload?.progress,
            data: action.payload?.result
          }
        }
      }
    },
    [ProcessBlackObjectsAnalysisActionTypes.REQUEST]: (state) => ({
      ...state,
      processBlackObjectsAnalysisStatus: AsyncStates.LOADING,
    }),
    [ProcessBlackObjectsAnalysisActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      processBlackObjectsAnalysis: action.payload ?? {},
      processBlackObjectsAnalysisStatus: AsyncStates.SUCCESS,
    }),
    [ProcessBlackObjectsAnalysisActionTypes.FAILURE]: (state, action) => ({
      ...state,
      processBlackObjectsAnalysisStatus: AsyncStates.ERROR,
      processBlackObjectsAnalysisError: action.payload,
    }),
    [ProcessBlackObjectsAnalysisActionTypes.CLEAR]: (state, action) => ({
      ...state,
      processBlackObjectsAnalysisStatus: AsyncStates.INITIAL,
    }),

    [FetchBlackObjectsBatchInfoActionTypes.REQUEST]: (state) => ({
      ...state,
      blackObjectsBatchInfoStatus: AsyncStates.LOADING,
    }),
    [FetchBlackObjectsBatchInfoActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      blackObjectsBatchInfoStatus: AsyncStates.SUCCESS,
      blackObjectsBatchInfo: action.payload ?? []
    }),
    [FetchBlackObjectsBatchInfoActionTypes.FAILURE]: (state, action) => ({
      ...state,
      blackObjectsBatchInfoStatus: AsyncStates.ERROR,
      blackObjectsBatchInfoError: action.payload,
    }),
    [FetchBlackObjectsBatchInfoActionTypes.CLEAR]: (state) => ({
      ...state,
      fiberBatchInfo: [],
      blackObjectsBatchInfoStatus: AsyncStates.INITIAL,
      blackObjectsBatchInfoError: "",
    }),

    [DeleteBlackObjectsBatchActionTypes.REQUEST]: (state) => ({
      ...state,
      deleteBlackObjectsBatchStatus: AsyncStates.LOADING,
    }),
    [DeleteBlackObjectsBatchActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      deleteBlackObjectsBatchStatus: AsyncStates.SUCCESS,
      deleteBlackObjectsBatch: action.payload ?? {},
    }),
    [DeleteBlackObjectsBatchActionTypes.FAILURE]: (state, action) => ({
      ...state,
      deleteBlackObjectsBatchStatus: AsyncStates.ERROR,
      deleteBlackObjectsBatchError: action.payload,
    }),
    [DeleteBlackObjectsBatchActionTypes.CLEAR]: (state, action) => ({
      ...state,
      deleteBlackObjectsBatchStatus: AsyncStates.INITIAL,
    }),

    [BlackObjectsRecalibrateScaleActionTypes.REQUEST]: (state) => ({
      ...state,
      blackObjectsRecalibrateScaleStatus: AsyncStates.LOADING,
    }),
    [BlackObjectsRecalibrateScaleActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      blackObjectsRecalibrateScaleStatus: AsyncStates.SUCCESS
    }),
    [BlackObjectsRecalibrateScaleActionTypes.FAILURE]: (state, action) => ({
      ...state,
      blackObjectsRecalibrateScaleStatus: AsyncStates.ERROR,
      blackObjectsRecalibrateScaleError: action.payload,
    }),
    [BlackObjectsRecalibrateScaleActionTypes.CLEAR]: (state, action) => ({
      ...state,
      blackObjectsRecalibrateScaleStatus: AsyncStates.INITIAL
    }),
    [ModifyBlackObjectsActionTypes.REQUEST]: (state) => ({
      ...state,
      modifyBlackObjectsStatus: AsyncStates.LOADING,
    }),
    [ModifyBlackObjectsActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      modifyBlackObjectsStatus: AsyncStates.SUCCESS,
    }),
    [ModifyBlackObjectsActionTypes.FAILURE]: (state, action) => ({
      ...state,
      modifyBlackObjectsStatus: AsyncStates.ERROR,
    }),
    [ModifyBlackObjectsActionTypes.CLEAR]: (state, action) => ({
      ...state,
      modifyBlackObjectsStatus: AsyncStates.INITIAL,
    }),
    [UpdatePhaseTwoFiberAnalysisPhaseActionTypes.REQUEST]: (state, action) => ({
      ...state,
      fiberAnalysisCurrentPhase: {
        ...(state.fiberAnalysisCurrentPhase ?? {}),
        [action.payload.file_id]: action.payload.phase
      },
    }),

    [UpdatePhaseTwoFiberAnalysisPhaseActionTypes.CLEAR]: (state, action) => ({
      ...state,
      fiberAnalysisCurrentPhase: {
        ...(state.fiberAnalysisCurrentPhase ?? {}),
        [action.payload.file_id]: null
      },
    }),
    [DownloadFiberAnalysisPhaseTwoReportActionTypes.REQUEST]: (state) => ({
      ...state,
      fiberAnalysisPhaseTwoReportDownloadStatus: AsyncStates.LOADING,
    }),
    [DownloadFiberAnalysisPhaseTwoReportActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      fiberAnalysisPhaseTwoReportDownloadStatus: AsyncStates.SUCCESS,
    }),
    [DownloadFiberAnalysisPhaseTwoReportActionTypes.FAILURE]: (state, action) => ({
      ...state,
      fiberAnalysisPhaseTwoReportDownloadStatus: AsyncStates.ERROR,
    }),
    [GetFiberAnalysisPhaseTwoParametersActionTypes.REQUEST]: (state) => ({
      ...state,
      fiberAnalysisPhaseTwoParametersStatus: AsyncStates.LOADING
    }),
    [GetFiberAnalysisPhaseTwoParametersActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      fiberAnalysisPhaseTwoParameters: action.payload,
      fiberAnalysisPhaseTwoParametersStatus: AsyncStates.SUCCESS,

    }),
    [GetFiberAnalysisPhaseTwoParametersActionTypes.FAILURE]: (state, action) => ({
      ...state,
      fiberAnalysisPhaseTwoParametersStatus: AsyncStates.ERROR,
    }),
  },
  defaultState
);

export default semAnalysisReducer;
